/*----------------------------------
- COMPONENT: PUBLIC DOMAINS PREVIEW BLOCK
----------------------------------*/

/**
 * @docs docs/features/product-domain-listings
 * @adr  ADR-0028
 * @rule Both public previews, the `/domains*` listing and the Home preview, render through this
 *       one block: the same table, the same phone cards and the same row drawer, so a row can
 *       never read differently on the two pages. A row click opens the drawer; only the drawer's
 *       CTA hands off to authentication, through the caller's own conversion path.
 *
 * Owns what the two pages used to duplicate: the row shaping, the fill counts, the count label,
 * the phone switch and its hydration gate, and the drawer state.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import React from 'react';

import { countFilledCells } from '@uniquedomains/components/domains/table/cellValues';
import { useMobileViewport } from '@uniquedomains/components/runtime/useMediaQuery';
import { PUBLIC_DOMAINS_TABLE_COLUMNS } from '@/client/catalogs/landing/domains/publicTableColumns';
import PublicDomainsTable, { type PublicDomainsTableRow } from './table/PublicDomainsTable';
import PublicDomainsRowCards from './cards/PublicDomainsRowCards';
import LazyPublicDomainRowDrawer from './drawer/LazyPublicDomainRowDrawer';
import usePublicRowPreviewDrawer from '../lib/usePublicRowPreviewDrawer';

// Types
import type { WebsitePublicSearchProjection } from '@/common/types/domains/publicSearch';
import type { WebsiteDomainsSearchRow } from '@/common/types/domains/websiteLanding';
import type { DatabaseBrowseCountPrecision } from '@uniquedomains/components/contracts/domains/browseFacets';

/*----------------------------------
- TYPES
----------------------------------*/

type DrawerOptions = Parameters<typeof usePublicRowPreviewDrawer>[0];

type PublicDomainsPreviewBlockProps = Pick<DrawerOptions, 'page' | 'section' | 'triggerId'> & {
    /** The rows to draw, already capped by the caller (twenty on the listing, ten on Home). */
    rows: readonly WebsiteDomainsSearchRow[];
    projection: WebsitePublicSearchProjection | null;
    /** The search's total count, formatted by the caller, or null when there is none to show. */
    totalCountLabel: string | null;
    /** How the total was counted, for the spoken label beside it. */
    countPrecision: DatabaseBrowseCountPrecision;
    /**
     * True when `rows` are the illustrative placeholders a search with no results draws. A click
     * on one of those stays See all: previewing it would present a domain the visitor never
     * really saw listed.
     */
    rowsArePlaceholders?: boolean;
    onSeeAll: () => void;
    /** The drawer's CTA, naming the row: the caller opens authentication with the domain carried. */
    onUnlockRow: (domain: string) => void;
    /** The table row "+", naming the row. Phone cards draw no "+", so only the table calls it. */
    onAddRow: (domain: string) => void;
};

/*----------------------------------
- COMPONENT
----------------------------------*/

const PublicDomainsPreviewBlock = ({
    rows,
    projection,
    totalCountLabel,
    countPrecision,
    rowsArePlaceholders = false,
    page,
    section,
    triggerId,
    onSeeAll,
    onUnlockRow,
    onAddRow,
}: PublicDomainsPreviewBlockProps) => {
    /** False through hydration, true from the first update: see `isPhoneLayout` below. */
    const [isMounted, setIsMounted] = React.useState(false);
    React.useEffect(() => {
        setIsMounted(true);
    }, []);

    const tableRows = React.useMemo<PublicDomainsTableRow[]>(() => {
        const projectionRows = projection?.rows ?? {};
        return rows.map((row, index) => ({
            key: row.id || `${row.domain}-${index}`,
            row,
            projection: row.id ? projectionRows[row.id] ?? null : null,
        }));
    }, [projection?.rows, rows]);
    /* Counted from the rows on screen, so the footer describes this preview and never the catalog. */
    const fillCounts = React.useMemo(
        () =>
            countFilledCells(
                tableRows.map((entry) => ({ row: entry.row, projection: entry.projection })),
                PUBLIC_DOMAINS_TABLE_COLUMNS.map((column) => column.id),
            ),
        [tableRows],
    );
    const { previewRow, openPreview, closePreview, showPrevious, showNext } = usePublicRowPreviewDrawer({
        page,
        section,
        triggerId,
        rows: tableRows,
    });
    const handleOpenRow = (domain: string) => {
        if (rowsArePlaceholders) {
            onSeeAll();
            return;
        }
        const entry = tableRows.find(({ row }) => row.domain === domain);
        if (entry) openPreview(entry);
    };
    /* Same placeholder rule as a row click: saving an illustrative name would carry a domain the
       visitor never saw listed into the wall and the Product landing URL. */
    const handleAddRow = (domain: string) => {
        if (rowsArePlaceholders) {
            onSeeAll();
            return;
        }
        onAddRow(domain);
    };

    const isPhoneViewport = useMobileViewport();
    /*
     * The swap waits for the mount, it never happens during hydration.
     *
     * `useMediaQuery` reads matchMedia in its lazy initializer, which is right for a sheet that
     * mounts on demand and wrong here: a hydrating tree that renders cards against SSR'd table
     * markup makes Preact reuse the nodes it finds positionally, and the page came back with card
     * content wearing the table footer's classes. Rendering the SSR branch on frame one and
     * switching on the next makes it an ordinary update, which replaces the subtree cleanly.
     */
    const isPhoneLayout = isMounted && isPhoneViewport;

    return (
        <>
            {isPhoneLayout ? (
                <PublicDomainsRowCards
                    rows={tableRows}
                    columns={PUBLIC_DOMAINS_TABLE_COLUMNS}
                    totalCountLabel={totalCountLabel}
                    countPrecision={countPrecision}
                    onOpenRow={handleOpenRow}
                    onSeeAll={onSeeAll}
                />
            ) : (
                <PublicDomainsTable
                    rows={tableRows}
                    columns={PUBLIC_DOMAINS_TABLE_COLUMNS}
                    fillCounts={fillCounts}
                    totalCountLabel={totalCountLabel}
                    countPrecision={countPrecision}
                    onOpenRow={handleOpenRow}
                    onAddRow={handleAddRow}
                    onSeeAll={onSeeAll}
                />
            )}
            {/* One drawer for both presentations: the table and the phone cards share `handleOpenRow`.
                Its CTA closes the drawer before calling `onUnlockRow`, so the auth modal it opens in
                Proteum's host (z-index 200) is never covered by the drawer (z-index 2147483646). */}
            <LazyPublicDomainRowDrawer
                row={previewRow}
                onRequestClose={closePreview}
                onSeeFullReport={onUnlockRow}
                onPrevious={showPrevious}
                onNext={showNext}
            />
        </>
    );
};

export default PublicDomainsPreviewBlock;
